import { CombinedGraphQLErrors, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useRef, useEffect, useState } from 'react';

import { TeacherStatus } from 'config/teacherProfiles';
import { School } from 'model/misc';
import { Permissions } from 'model/profiles';
import { UserTeacherPermissionsGql } from 'model/User';

const TEACHERFIRST_INIT_GQL = gql`
  query TeacherfirstInitQuery($userId: ID!, $permissions: [String!], $teacherId: ID, $teacherStatus: [String!]) {
    user(id: $userId, permissions: $permissions) {
      id
      lastName
      firstName
      email
      roles {
        role
        resourceType
        resourceId
        id
      }
      isActive
      teacherProfiles(teacherId: $teacherId, teacherStatus: $teacherStatus) {
        id
        lastName
        firstName
        email
        emailSubscribed
        department {
          id
          name
          school {
            id
            name
            chatConfig {
              enabled
            }
            addiChatConfig {
              enabled
            }
            helpCenterConfig {
              enabled
            }
            piiConfig {
              studentEmailHidden
            }
            teacherDashboardConfig {
              assignmentsEnabled
            }
          }
        }
        personalInterests
        instructionLanguages
        teacherSkills {
          id
        }
        subjects {
          code
          description
          id
          state
        }
        status
        profilePicturePath
        terminationDate
        downgradeDate
        path
        role
        permissions {
          permission
          resourceId
          resourceType
        }
      }
    }
    schools {
      edges {
        id
        name
        chatConfig {
          enabled
        }
        addiChatConfig {
          enabled
        }
        helpCenterConfig {
          enabled
        }
        piiConfig {
          studentEmailHidden
        }
        teacherDashboardConfig {
          assignmentsEnabled
        }
      }
    }
  }
`;

export const useTeacherfirstInit = (skip?: boolean) => {
  const hasRetriedRef = useRef(false);
  const [hasRetried, setHasRetried] = useState(false);

  const queryResult = useQuery<{ user: UserTeacherPermissionsGql; schools: { edges: School[] } }>(
    TEACHERFIRST_INIT_GQL,
    {
      variables: {
        userId: 'me',
        teacherStatus: [TeacherStatus.ACTIVE, TeacherStatus.PENDING_DOWNGRADE],
        permissions: [
          Permissions.VIEW_TEACHERS_PORTAL,
          Permissions.VIEW_OFFLINE_TEACHERS_DASHBOARD,
          Permissions.VIEW_TEACHERS,
          Permissions.VIEW_INVOICES,
          Permissions.VIEW_CLASS_RECORDS,
          Permissions.SUBMIT_INVOICES,
          Permissions.CREATE_INVOICES,
          Permissions.VIEW_AVAILABILITY,
          Permissions.EDIT_AVAILABILITY,
          Permissions.EDIT_TEACHER_INSTRUCTION_LANGUAGES,
          Permissions.EDIT_TEACHER_PERSONAL_INTERESTS,
          Permissions.EDIT_TEACHER_SUBJECTS,
          Permissions.VIEW_BOOKING_EXIT_SURVEY,
          Permissions.VIEW_PEAK_RANGES,
          Permissions.CREATE_BOOKING_BY_TEACHER,
          Permissions.VIEW_EXCLUSIVE_AVAILABILITY,
          Permissions.EDIT_EXCLUSIVE_AVAILABILITY,
          Permissions.EDIT_RESERVE_TIMESLOT_SETTINGS,
          Permissions.SUBOUT_BOOKING,
          Permissions.VIEW_UNCLAIMED_BOOKINGS,
          Permissions.CHATROOM_CREATE,
        ],
      },
      skip,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  );

  const { error, refetch } = queryResult;

  // Apollo Client 4: Use useEffect for error handling instead of onError callback
  useEffect(() => {
    if (!error) {
      return;
    }

    const isUnauthorized =
      CombinedGraphQLErrors.is(error) &&
      error?.errors?.some(
        (err) =>
          err.extensions?.code === 'UNAUTHENTICATED' ||
          (err.extensions?.response as { status?: number })?.status === 401 ||
          err.message?.includes('401'),
      );

    if (!isUnauthorized) {
      return;
    }

    if (!hasRetriedRef.current) {
      // First 401: may be transient (first-time login MSAL setup), wait and retry
      hasRetriedRef.current = true;
      const timeoutId = setTimeout(() => {
        void refetch().finally(() => {
          setHasRetried(true);
        });
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [error, refetch]);

  return {
    ...queryResult,
    hasRetried,
  };
};
